import { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { ShieldCheck, Sparkles } from 'lucide-react';
import { HankoStamp } from '../ui/HankoStamp';

/**
 * Right-rail product card stack used in the hero. Three layered cards:
 * AP invoice with qualified-invoice + AI suggestion, ringi approval with
 * stacked hanko, and a cross-border payment line.
 */
export function HeroProductMock() {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);

  // Scroll-linked morphing: as the user scrolls past the hero, each card
  // shifts horizontally + rotates slightly — the spec's "workspace mock
  // that morphs as the user scrolls" requirement, executed with Framer
  // Motion instead of Lottie so it ships in pure JS.
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] });
  const apX = useTransform(scrollYProgress, [0, 0.6], [0, -32]);
  const apRot = useTransform(scrollYProgress, [0, 0.6], [-1.4, -4]);
  const ringiX = useTransform(scrollYProgress, [0.1, 0.7], [0, 18]);
  const ringiRot = useTransform(scrollYProgress, [0.1, 0.7], [0.6, 2.4]);
  const payX = useTransform(scrollYProgress, [0.2, 0.8], [0, -16]);
  const payRot = useTransform(scrollYProgress, [0.2, 0.8], [-0.8, -3.2]);

  return (
    <div className="relative" ref={ref}>
      {/* Soft ink panel behind the cards */}
      <div className="absolute -inset-3 rounded-3xl bg-gradient-to-br from-card via-pearl-50 to-pearl-100 shadow-washi" />

      <div className="relative space-y-3 p-3">
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55 }}
          style={{ x: apX, rotate: apRot }}
          className="rounded-xl bg-card border border-border p-4 shadow-soft"
        >
          <div className="flex items-start justify-between">
            <div>
              <div className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
                {t('landing.heroCardApLabel')} · INV-2026-04-119
              </div>
              <div className="font-medium mt-0.5">株式会社 サクラ広告</div>
            </div>
            <span className="num text-lg font-semibold">¥548,000</span>
          </div>
          <div className="mt-3 flex items-center gap-2 text-xs flex-wrap">
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700">
              <ShieldCheck size={12} /> {t('landing.heroCardApQualified')}
            </span>
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-accent-50 text-accent-700">
              <Sparkles size={12} /> {t('landing.heroCardApSuggestion')}
            </span>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, delay: 0.12 }}
          style={{ x: ringiX, rotate: ringiRot }}
          className="rounded-xl bg-card border border-border p-4 shadow-soft"
        >
          <div className="flex items-start justify-between">
            <div>
              <div className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
                {t('landing.heroCardRingiLabel')}
              </div>
              <div className="font-medium mt-0.5">{t('landing.heroCardRingiFlow')}</div>
            </div>
            <div className="flex gap-2">
              <HankoStamp text="田中" size={42} />
              <HankoStamp text="佐藤" size={42} animate />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, delay: 0.24 }}
          style={{ x: payX, rotate: payRot }}
          className="rounded-xl bg-card border border-border p-4 shadow-soft"
        >
          <div className="flex items-start justify-between">
            <div>
              <div className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
                {t('landing.heroCardCrossLabel')}
              </div>
              <div className="font-medium mt-0.5">{t('landing.heroCardCrossSample')}</div>
            </div>
            <span className="num text-lg font-semibold">₱45,000</span>
          </div>
          <div className="mt-3 text-xs text-muted-foreground">
            {t('landing.heroCardCrossVia')} · {t('landing.heroCardCrossW8')}
          </div>
        </motion.div>
      </div>
    </div>
  );
}
